import React, { useState, useMemo } from "react"
import { toast } from "sonner"
import type { FlatMapping, FilterMode } from "@/types/ui"
import type { ValueMapping, MappingConfig, FieldMapping } from "@/types/fbo-api"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

interface UseMappingManagementProps {
  allMappings: FlatMapping[]
  config: MappingConfig
  onConfigChange: (config: MappingConfig) => void
  onSave?: () => void
  hasInvalidMappings: boolean
}

export function useMappingManagement({
  allMappings,
  config,
  onConfigChange,
  onSave,
  hasInvalidMappings
}: UseMappingManagementProps) {
  const { t } = useTranslation()

  // State management
  const [searchQuery, setSearchQuery] = useState("")
  const [editingMapping, setEditingMapping] = useState<FlatMapping | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [filterMode, setFilterMode] = useState<FilterMode>("mapped")

  // Data processing
  const filteredMappings = useMemo(() => {
    const query = searchQuery.toLowerCase().trim()
    
    return allMappings
      .filter((mapping) => {
        // Apply filter mode
        if (filterMode === "mapped") return mapping.isMapped
        if (filterMode === "unmapped") return !mapping.isMapped
        if (filterMode === "invalid") return mapping.isInvalid
        return true // "all"
      })
      .filter((mapping) => {
        // Apply search query
        if (!query) return true
        
        return (
          mapping.destinationField.toLowerCase().includes(query) ||
          (mapping.sourceField && mapping.sourceField.toLowerCase().includes(query)) ||
          (mapping.entityName && mapping.entityName.toLowerCase().includes(query)) ||
          (mapping.valueMappings && mapping.valueMappings.some(vm => 
            JSON.stringify(vm.sourceValue).toLowerCase().includes(query) ||
            JSON.stringify(vm.destinationValue).toLowerCase().includes(query)
          ))
        )
      })
  }, [allMappings, searchQuery, filterMode])

  // Reset to "all" if currently on "invalid" filter but no invalid mappings remain
  React.useEffect(() => {
    if (filterMode === "invalid" && !hasInvalidMappings) {
      setFilterMode("all")
    }
  }, [filterMode, hasInvalidMappings])

  const unmappedDestinationFields = useMemo(() => {
    return allMappings
      .filter((mapping) => !mapping.isMapped)
      .map((mapping) => mapping.destinationField)
  }, [allMappings])

  // Utility function to extract FieldMapping properties from FlatMapping
  const createFieldMappingFromFlat = (mapping: FlatMapping): FieldMapping => {
    const fieldMapping: FieldMapping = {
      sourceField: mapping.sourceField!,
      destinationField: mapping.destinationField,
    }
    
    // Add optional properties if they exist
    if (mapping.dataTypeConverter) {
      fieldMapping.dataTypeConverter = mapping.dataTypeConverter
    }
    if (mapping.valueMappings && mapping.valueMappings.length > 0) {
      fieldMapping.valueMappings = mapping.valueMappings
    }
    if (mapping.valueMappingSetName) {
      fieldMapping.valueMappingSetName = mapping.valueMappingSetName
    }
    if (mapping.defaultValueMapping !== undefined && mapping.defaultValueMapping !== null) {
      fieldMapping.defaultValueMapping = mapping.defaultValueMapping
    }
    
    return fieldMapping
  }

  // Event handlers
  const handleSave = () => {
    if (onSave) {
      onSave()
    }
  }

  const handleAddMapping = (mapping: FlatMapping) => {
    if (mapping.entityIndex === null || !mapping.sourceField) {
      return // Invalid state for adding
    }
    // Check if the destination field is already mapped
    if (allMappings.some((m) => m.destinationField === mapping.destinationField && m.isMapped && !m.isInvalid)) {
        toast.error(t("destinationFields.toasts.errorTitle"), {
          description: t("destinationFields.toasts.alreadyMapped", { field: mapping.destinationField }),
        })
        return
    }

    const updatedConfig = JSON.parse(JSON.stringify(config))
    const newFieldMapping = createFieldMappingFromFlat(mapping)
    updatedConfig.entities[mapping.entityIndex].fieldMappings.push(newFieldMapping)

    onConfigChange(updatedConfig)
    toast.success(t("destinationFields.toasts.addedTitle"), {
      description: t("destinationFields.toasts.added", { source: mapping.sourceField, destination: mapping.destinationField }),
    })
  }

  const handleRemoveMapping = (entityIndex: number | null, mappingIndex: number | null) => {
    if (entityIndex === null || mappingIndex === null) {
      return
    }
    const updatedConfig = JSON.parse(JSON.stringify(config))
    updatedConfig.entities[entityIndex].fieldMappings.splice(mappingIndex, 1)
    onConfigChange(updatedConfig)
    toast.success(t("destinationFields.toasts.removedTitle"), {
      description: t("destinationFields.toasts.removed"),
    })
  }

  const handleUpdateMapping = (mapping: FlatMapping) => {
    if (!editingMapping || !editingMapping.isMapped || editingMapping.entityIndex === null || editingMapping.mappingIndex === null) {
        return
    }

    const updatedConfig = JSON.parse(JSON.stringify(config))

    // Remove old mapping and add updated mapping
    updatedConfig.entities[editingMapping.entityIndex].fieldMappings.splice(editingMapping.mappingIndex, 1)
    const updatedFieldMapping = createFieldMappingFromFlat(mapping)
    updatedConfig.entities[mapping.entityIndex!].fieldMappings.push(updatedFieldMapping)

    onConfigChange(updatedConfig)
    setEditingMapping(null)
    
    toast.success(t("destinationFields.toasts.updatedTitle"), {
      description: t("destinationFields.toasts.updated"),
    })
  }

  const openMappingDialog = (mapping: FlatMapping) => {
    setEditingMapping(mapping)
  }

  const handleMappingDialogSave = (updatedMapping: {
    destinationField: string
    sourceField: string
    entityIndex: number
    valueMappings?: ValueMapping[]
    valueMappingSetName?: string | null
    dataTypeConverter?: string | null
    defaultValueMapping?: string | number | null
  }) => {
    if (!editingMapping) return

    // Create the full FlatMapping with updated values
    const fullMapping: FlatMapping = {
      ...editingMapping,
      destinationField: updatedMapping.destinationField,
      sourceField: updatedMapping.sourceField,
      entityIndex: updatedMapping.entityIndex,
      entityName: config.entities[updatedMapping.entityIndex]?.name || "",
      valueMappings: updatedMapping.valueMappings,
      valueMappingSetName: updatedMapping.valueMappingSetName,
      dataTypeConverter: updatedMapping.dataTypeConverter,
      defaultValueMapping: updatedMapping.defaultValueMapping,
    }

    if (!editingMapping.isMapped) {
      // Add mode
      handleAddMapping(fullMapping)
    } else {
      // Edit mode
      handleUpdateMapping(fullMapping)
    }
    
    setEditingMapping(null)
  }

  const toggleRowExpansion = (key: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(key)) {
      newExpanded.delete(key)
    } else {
      newExpanded.add(key)
    }
    setExpandedRows(newExpanded)
  }

    return {
    // State
    searchQuery,
    setSearchQuery,
    editingMapping,
    setEditingMapping,
    expandedRows,
    filterMode,
    setFilterMode,
    
    // Computed values
    filteredMappings,
    unmappedDestinationFields,
    
    // Event handlers
    handleSave,
    handleMappingDialogSave,
    handleRemoveMapping,
    openMappingDialog,
    toggleRowExpansion
  }
}
